import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../supabase-config";
import {
  ADMIN_COLLECTIONS,
  LANDING_MEDIA_COLLECTION,
  LANDING_MEDIA_SLOTS,
  deleteItem,
  listCollection,
  listLandingMedia,
  saveCollectionOrder,
  saveLandingMediaSlot,
  upsertItem,
  uploadWorkImage,
} from "../../service/adminApi";
import { emailToUsername, usernameToEmail } from "../../service/adminAuth";
import "./Admin.css";

function emptyDraft(collection) {
  return {
    id: `${Date.now()}`,
    title: "",
    description: "",
    link: "",
    producer: "",
    date: "",
    ImageUrl: "",
    ImageIsScoresHome: "true",
    sort_order: 0,
    _collection: collection,
  };
}

function LoginForm({ onLoggedIn }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const email = usernameToEmail(username);
    if (!email) {
      setBusy(false);
      setError("Enter a valid username");
      return;
    }
    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setBusy(false);
    if (authError) {
      setError(authError.message);
      return;
    }
    onLoggedIn();
  };

  return (
    <div className="admin-shell admin-shell--login">
      <form className="admin-login" onSubmit={submit}>
        <p className="admin-kicker">Giustini Composer</p>
        <h1>Admin</h1>
        <label>
          Username
          <input
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && <p className="admin-error">{error}</p>}
        <button type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/" className="admin-back">
          ← Back to site
        </Link>
      </form>
    </div>
  );
}

function Editor({ draft, onChange, onSave, onCancel, onUpload, busy, message }) {
  return (
    <aside className="admin-editor">
      <header>
        <h2>{draft._isNew ? "New item" : "Edit item"}</h2>
        <button type="button" className="admin-ghost" onClick={onCancel}>
          Close
        </button>
      </header>

      <label>
        ID
        <input
          value={draft.id}
          onChange={(e) => onChange({ ...draft, id: e.target.value })}
          disabled={!draft._isNew}
        />
      </label>
      <label>
        Title
        <input
          value={draft.title || ""}
          onChange={(e) => onChange({ ...draft, title: e.target.value })}
        />
      </label>
      <label>
        Description
        <textarea
          rows={4}
          value={draft.description || ""}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
        />
      </label>
      <label>
        Link
        <input
          value={draft.link || ""}
          onChange={(e) => onChange({ ...draft, link: e.target.value })}
        />
      </label>
      <label>
        Producer
        <input
          value={draft.producer || ""}
          onChange={(e) => onChange({ ...draft, producer: e.target.value })}
        />
      </label>
      <label>
        Date
        <input
          value={draft.date || ""}
          onChange={(e) => onChange({ ...draft, date: e.target.value })}
        />
      </label>

      {draft._collection !== "credits" && (
        <label className="admin-check">
          <input
            type="checkbox"
            checked={
              draft.ImageIsScoresHome == null ||
              String(draft.ImageIsScoresHome).toLowerCase() !== "false"
            }
            onChange={(e) =>
              onChange({
                ...draft,
                ImageIsScoresHome: e.target.checked ? "true" : "false",
              })
            }
          />
          Show on landing
        </label>
      )}
      {draft._collection !== "credits" && (
        <p className="admin-hint">
          Uncheck to hide this work from the home strip and from the category
          previews. Use the arrows in the list to choose which images appear
          first.
        </p>
      )}

      <div className="admin-image-block">
        {draft.ImageUrl ? (
          <img src={draft.ImageUrl} alt="" />
        ) : (
          <div className="admin-image-empty">No image</div>
        )}
        <label className="admin-file">
          Upload image
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onUpload(file);
              e.target.value = "";
            }}
          />
        </label>
        <label>
          Image URL
          <input
            value={draft.ImageUrl || ""}
            onChange={(e) => onChange({ ...draft, ImageUrl: e.target.value })}
          />
        </label>
      </div>

      {message && <p className="admin-msg">{message}</p>}

      <div className="admin-editor-actions">
        <button type="button" onClick={onSave} disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </aside>
  );
}

function LandingMediaEditor() {
  const [slots, setSlots] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [messages, setMessages] = useState({});

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listLandingMedia();
      setSlots(data);
      setDrafts(
        Object.fromEntries(data.map((slot) => [slot.id, slot.ImageUrl || ""]))
      );
    } catch (err) {
      setError(err.message || String(err));
      setSlots(LANDING_MEDIA_SLOTS);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const setUrl = (id, value) => {
    setDrafts((current) => ({ ...current, [id]: value }));
  };

  const save = async (id) => {
    setBusyId(id);
    setMessages((current) => ({ ...current, [id]: "" }));
    try {
      await saveLandingMediaSlot(id, drafts[id] || "");
      setMessages((current) => ({ ...current, [id]: "Saved" }));
      await load();
    } catch (err) {
      setMessages((current) => ({
        ...current,
        [id]: err.message || String(err),
      }));
    }
    setBusyId("");
  };

  const onUpload = async (id, file) => {
    if (!file) return;
    setBusyId(id);
    setMessages((current) => ({ ...current, [id]: "Uploading…" }));
    try {
      const url = await uploadWorkImage(LANDING_MEDIA_COLLECTION, id, file);
      setDrafts((current) => ({ ...current, [id]: url }));
      await saveLandingMediaSlot(id, url);
      setMessages((current) => ({ ...current, [id]: "Saved" }));
      await load();
    } catch (err) {
      setMessages((current) => ({
        ...current,
        [id]: err.message || String(err),
      }));
    }
    setBusyId("");
  };

  return (
    <div className="admin-landing">
      <div className="admin-toolbar">
        <h2>Landing images</h2>
      </div>
      <p className="admin-hint admin-hint--page">
        These three photos are on the homepage quote and magazine sections.
        Upload here, or edit the <code>landing_media</code> table in Supabase
        (<code>quote_a</code>, <code>quote_b</code>, <code>magazine</code>).
      </p>
      {error && <p className="admin-error">{error}</p>}
      {loading ? (
        <p className="admin-muted">Loading…</p>
      ) : (
        <div className="admin-landing-grid">
          {slots.map((slot) => {
            const url = drafts[slot.id] || "";
            const busy = busyId === slot.id;
            return (
              <article key={slot.id} className="admin-landing-card">
                <header>
                  <h3>{slot.label}</h3>
                  <p>{slot.hint}</p>
                </header>
                <div className="admin-image-block">
                  {url ? (
                    <img src={url} alt="" />
                  ) : (
                    <div className="admin-image-empty">No image</div>
                  )}
                  <label className="admin-file">
                    Upload image
                    <input
                      type="file"
                      accept="image/*"
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) onUpload(slot.id, file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  <label>
                    Image URL
                    <input
                      value={url}
                      onChange={(e) => setUrl(slot.id, e.target.value)}
                    />
                  </label>
                </div>
                {messages[slot.id] && (
                  <p className="admin-msg">{messages[slot.id]}</p>
                )}
                <div className="admin-editor-actions">
                  <button
                    type="button"
                    onClick={() => save(slot.id)}
                    disabled={busy}
                  >
                    {busy ? "Saving…" : "Save"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Dashboard({ session, onSignOut }) {
  const [section, setSection] = useState("landing");
  const [collection, setCollection] = useState(ADMIN_COLLECTIONS[0].key);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [orderDirty, setOrderDirty] = useState(false);
  const [orderMsg, setOrderMsg] = useState("");

  const label = useMemo(
    () => ADMIN_COLLECTIONS.find((c) => c.key === collection)?.label || collection,
    [collection]
  );

  const load = async (key = collection) => {
    setLoading(true);
    setError("");
    setOrderDirty(false);
    setOrderMsg("");
    try {
      const data = await listCollection(key);
      setItems(data);
    } catch (err) {
      setError(err.message || String(err));
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load(collection);
    setDraft(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection]);

  const openNew = () => {
    setMessage("");
    setDraft({
      ...emptyDraft(collection),
      sort_order: items.length,
      _isNew: true,
    });
  };

  const openEdit = (item) => {
    setMessage("");
    setDraft({ ...item, _collection: collection, _isNew: false });
  };

  const save = async () => {
    if (!draft?.id?.trim()) {
      setMessage("ID is required");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const { _isNew, _collection, ...payload } = draft;
      await upsertItem(collection, payload);
      setMessage("Saved");
      await load();
      setDraft((current) =>
        current ? { ...current, ...payload, _isNew: false } : current
      );
    } catch (err) {
      setMessage(err.message || String(err));
    }
    setBusy(false);
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete “${item.title || item.id}”?`)) return;
    setBusy(true);
    try {
      await deleteItem(collection, item.id);
      await load();
      if (draft?.id === item.id) setDraft(null);
    } catch (err) {
      setError(err.message || String(err));
    }
    setBusy(false);
  };

  const moveItem = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    next.splice(nextIndex, 0, moved);
    setItems(next.map((item, i) => ({ ...item, sort_order: i })));
    setOrderDirty(true);
    setOrderMsg("");
  };

  const saveOrder = async () => {
    setBusy(true);
    setOrderMsg("");
    setError("");
    try {
      const ordered = await saveCollectionOrder(collection, items);
      setItems(ordered);
      setOrderDirty(false);
      setOrderMsg("Order saved");
    } catch (err) {
      setError(err.message || String(err));
    }
    setBusy(false);
  };

  const onUpload = async (file) => {
    if (!draft?.id) {
      setMessage("Set an ID before uploading");
      return;
    }
    setBusy(true);
    setMessage("Uploading…");
    try {
      const url = await uploadWorkImage(collection, draft.id, file);
      setDraft((d) => ({ ...d, ImageUrl: url }));
      setMessage("Image uploaded — click Save");
    } catch (err) {
      setMessage(err.message || String(err));
    }
    setBusy(false);
  };

  return (
    <div className={`admin-shell${draft ? " admin-shell--editing" : ""}`}>
      <header className="admin-top">
        <div>
          <p className="admin-kicker">Giustini Composer</p>
          <h1>Admin</h1>
        </div>
        <div className="admin-top-actions">
          <span className="admin-user">
            {session.user?.user_metadata?.username ||
              emailToUsername(session.user?.email)}
          </span>
          <Link to="/">View site</Link>
          <button type="button" className="admin-ghost" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </header>

      <nav className="admin-tabs" aria-label="Collections">
        <button
          type="button"
          className={section === "landing" ? "is-active" : ""}
          onClick={() => {
            setSection("landing");
            setDraft(null);
          }}
        >
          Landing
        </button>
        {ADMIN_COLLECTIONS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={
              section === "works" && c.key === collection ? "is-active" : ""
            }
            onClick={() => {
              setSection("works");
              setCollection(c.key);
            }}
          >
            {c.label}
          </button>
        ))}
      </nav>

      {section === "landing" ? (
        <LandingMediaEditor />
      ) : (
        <>

      <div className="admin-toolbar">
        <h2>{label}</h2>
        <div className="admin-toolbar-actions">
          {orderDirty && (
            <button type="button" onClick={saveOrder} disabled={busy}>
              {busy ? "Saving order…" : "Save order"}
            </button>
          )}
          <button type="button" onClick={openNew}>
            + New
          </button>
        </div>
      </div>
      <p className="admin-hint admin-hint--page">
        Uncheck “Show on landing” to hide a work from the home strip and the
        category preview images. Use ↑ ↓ to choose which images appear first.
      </p>

      {orderMsg && <p className="admin-msg">{orderMsg}</p>}
      {error && <p className="admin-error">{error}</p>}
      {loading ? (
        <p className="admin-muted">Loading…</p>
      ) : (
        <div className="admin-list">
          {items.map((item, index) => (
            <article key={item.id} className="admin-card">
              <div className="admin-order">
                <span className="admin-order-index">{index + 1}</span>
                <button
                  type="button"
                  className="admin-order-btn"
                  aria-label="Move up"
                  disabled={busy || index === 0}
                  onClick={() => moveItem(index, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="admin-order-btn"
                  aria-label="Move down"
                  disabled={busy || index === items.length - 1}
                  onClick={() => moveItem(index, 1)}
                >
                  ↓
                </button>
              </div>
              <button
                type="button"
                className="admin-card-main"
                onClick={() => openEdit(item)}
              >
                <div className="admin-card-thumb">
                  {item.ImageUrl ? (
                    <img src={item.ImageUrl} alt="" />
                  ) : (
                    <span>No img</span>
                  )}
                </div>
                <div>
                  <h3>{item.title || "Untitled"}</h3>
                  <p>{item.id}</p>
                </div>
              </button>
              <button
                type="button"
                className="admin-danger"
                onClick={() => remove(item)}
                disabled={busy}
              >
                Delete
              </button>
            </article>
          ))}
          {!items.length && <p className="admin-muted">No items yet.</p>}
        </div>
      )}

      {draft && (
        <Editor
          draft={draft}
          onChange={setDraft}
          onSave={save}
          onCancel={() => setDraft(null)}
          onUpload={onUpload}
          busy={busy}
          message={message}
        />
      )}
        </>
      )}
    </div>
  );
}

export default function AdminApp() {
  const [session, setSession] = useState(undefined);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (mounted) setSession(data.session ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (session === undefined) {
    return (
      <div className="admin-shell admin-shell--login">
        <p className="admin-muted">Loading…</p>
      </div>
    );
  }

  if (!session) {
    return <LoginForm onLoggedIn={() => {}} />;
  }

  return (
    <Dashboard
      session={session}
      onSignOut={() => supabase.auth.signOut()}
    />
  );
}
